/**
 * src/voices/WaterFlowVoice.js
 *
 * Conveys water flow speed via a broadband pipe-rush vibrotactile texture.
 *
 * Signal graph:
 *   osc[0..3] (sawtooth, detuned) → mixGain (÷4)
 *                                      → lfoGain  ← LFO × lfoDepth  (AM turbulence)
 *                                          → outputGain  (ADSR envelope)
 *                                              → destination
 *
 * Data mapping  speedRatio ∈ [0,1]
 *   0 = clogged / stopped → loud, high-turbulence (alarm)
 *   1 = full-bore flow    → quiet, smooth (background)
 *
 * The gain transition uses a logistic sigmoid with high steepness (k≈20)
 * so the change from normal→alert feels like a step, not a fade.
 * This matches the Weber-Fechner perceptual model for intensity discontinuities.
 */

import { applyADSR, releaseADSR } from "../utils/adsr.js";
import { sigmoidInv, clamp01, lerp } from "../utils/math.js";

export class WaterFlowVoice {
    // ── static contract (required by VoiceRegistry) ───────────────────────────

    static defaultParams() {
        return {
            freqBase: 65,
            detuneSpread: 22,

            lfoRateMin: 1.2,
            lfoRateMax: 14,
            lfoDepthMin: 0.018,
            lfoDepthMax: 0.72,

            gainNormal: 0.055,
            gainAlert: 0.5,
            threshold: 0.32,
            sigmoidK: 20,

            adsr: { attack: 0.08, decay: 0.12, sustain: 0.8, release: 0.22 },
        };
    }

    // ── metadata used by the demo UI ─────────────────────────────────────────

    static meta = {
        label: "Water Flow",
        liveLabel: "Speed Ratio",
        liveDesc: "0 = clogged   1 = full bore",
        liveDefault: 0.82,
        curveXLabel: "speed ratio",
        curves(p) {
            const alert = (s) => sigmoidInv(s, p.sigmoidK, p.threshold);
            return [
                {
                    key: "gain",
                    label: "Gain",
                    color: "#e2e8f0",
                    fn: (s) =>
                        p.gainNormal + (p.gainAlert - p.gainNormal) * alert(s),
                },
                {
                    key: "lfoR",
                    label: "LFO Rate (norm)",
                    color: "#c4b5fd",
                    fn: (s) =>
                        lerp(p.lfoRateMin, p.lfoRateMax, alert(s)) /
                        Math.max(p.lfoRateMax, 0.001),
                },
                {
                    key: "lfoD",
                    label: "LFO Depth",
                    color: "#67e8f9",
                    fn: (s) => lerp(p.lfoDepthMin, p.lfoDepthMax, alert(s)),
                },
            ];
        },
    };

    // ── constructor ───────────────────────────────────────────────────────────

    constructor(ctx, destination, cfg = {}) {
        this.ctx = ctx;
        const def = WaterFlowVoice.defaultParams();
        this.cfg = {
            ...def,
            ...cfg,
            adsr: { ...def.adsr, ...(cfg.adsr ?? {}) },
        };
        this._buildGraph(destination);
    }

    _buildGraph(destination) {
        const { ctx, cfg } = this;
        const now = ctx.currentTime;

        this.outputGain = ctx.createGain();
        this.outputGain.gain.setValueAtTime(0, now);
        this.outputGain.connect(destination);

        // AM modulation stage — LFO drives this gain's .gain param
        this.lfoGain = ctx.createGain();
        this.lfoGain.gain.setValueAtTime(1.0, now);
        this.lfoGain.connect(this.outputGain);

        // Mix bus normalises the 4-osc bank to unity
        this.mixGain = ctx.createGain();
        this.mixGain.gain.setValueAtTime(0.25, now);
        this.mixGain.connect(this.lfoGain);

        // Four detuned sawtooth oscillators: offsets −1.5, −0.5, +0.5, +1.5 × spread
        this.oscs = [-1.5, -0.5, 0.5, 1.5].map((mult) => {
            const osc = ctx.createOscillator();
            osc.type = "sawtooth";
            osc.frequency.setValueAtTime(
                cfg.freqBase + mult * cfg.detuneSpread,
                now,
            );
            osc.connect(this.mixGain);
            osc.start();
            return osc;
        });

        // LFO for amplitude modulation (turbulence)
        this._lfo = ctx.createOscillator();
        this._lfo.type = "sine";
        this._lfo.frequency.setValueAtTime(cfg.lfoRateMin, now);

        this._lfoDepth = ctx.createGain();
        this._lfoDepth.gain.setValueAtTime(cfg.lfoDepthMin, now);

        this._lfo.connect(this._lfoDepth);
        this._lfoDepth.connect(this.lfoGain.gain);
        this._lfo.start();
    }

    // ── internal DSP helpers ──────────────────────────────────────────────────

    _alert(s) {
        return sigmoidInv(clamp01(s), this.cfg.sigmoidK, this.cfg.threshold);
    }
    _peakGain(s) {
        return lerp(this.cfg.gainNormal, this.cfg.gainAlert, this._alert(s));
    }
    _lfoRate(s) {
        return lerp(this.cfg.lfoRateMin, this.cfg.lfoRateMax, this._alert(s));
    }
    _lfoDepthFunc(s) {
        return lerp(this.cfg.lfoDepthMin, this.cfg.lfoDepthMax, this._alert(s));
    }

    // ── public interface ──────────────────────────────────────────────────────

    start(speedRatio = 0.8) {
        this._lfo.frequency.setValueAtTime(
            this._lfoRate(speedRatio),
            this.ctx.currentTime,
        );
        this._lfoDepth.gain.setValueAtTime(
            this._lfoDepthFunc(speedRatio),
            this.ctx.currentTime,
        );
        applyADSR(
            this.outputGain,
            this.ctx,
            this.cfg.adsr,
            this._peakGain(speedRatio),
        );
    }

    update(speedRatio) {
        const now = this.ctx.currentTime;
        const sustain = this._peakGain(speedRatio) * this.cfg.adsr.sustain;
        const g = this.outputGain.gain;

        // 50 ms ramp preserves the near-step perceptual quality on gain
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(sustain, now + 0.05);

        // LFO transitions slightly slower — avoids jarring rate jumps
        this._lfo.frequency.linearRampToValueAtTime(
            this._lfoRate(speedRatio),
            now + 0.12,
        );
        this._lfoDepth.gain.linearRampToValueAtTime(
            this._lfoDepth(speedRatio),
            now + 0.12,
        );
    }

    stop(onDone) {
        const silentAt = releaseADSR(this.outputGain, this.ctx, this.cfg.adsr);
        const delay = Math.max(
            0,
            (silentAt - this.ctx.currentTime) * 1000 + 20,
        );
        setTimeout(() => {
            this.dispose();
            onDone?.();
        }, delay);
    }

    dispose() {
        try {
            this.oscs.forEach((o) => o.stop());
            this._lfo.stop();
        } catch (_) {}
        this.oscs.forEach((o) => o.disconnect());
        this._lfo.disconnect();
        this._lfoDepth.disconnect();
        this.mixGain.disconnect();
        this.lfoGain.disconnect();
        this.outputGain.disconnect();
    }
}
